import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Fade
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  TrendingUp, 
  TrendingDown, 
  CalendarToday, 
  Analytics,
  FilterList,
  Timeline,
  Insights,
  Info
} from '@mui/icons-material';
import DynamicMonthlyChart from './DynamicMonthlyChart';

// Modern animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
`;

const SectionContainer = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '0',
  background: 'white',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  marginBottom: '16px',
  overflow: 'hidden',
  animation: `${slideInUp} 0.8s ease-out`,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    borderRadius: '16px',
    marginBottom: '24px'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '32px'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
  borderBottom: '1px solid rgba(11, 145, 214, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    padding: '20px 24px',
    gap: '16px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px 32px',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '18px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  }
}));

const FilterControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  flexWrap: 'wrap',
  [theme.breakpoints.up('sm')]: {
    gap: '12px'
  },
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    justifyContent: 'flex-end'
  }
}));

const TimeRangeButton = styled(Button)(({ theme, selected }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  minWidth: '60px',
  minHeight: '36px',
  transition: 'all 0.3s ease',
  backgroundColor: selected ? '#0B91D6' : 'transparent',
  color: selected ? 'white' : '#666',
  border: `1px solid ${selected ? '#0B91D6' : 'rgba(11, 145, 214, 0.2)'}`,
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.95)'
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '13px',
    borderRadius: '8px',
    padding: '8px 16px',
    minWidth: '80px',
    minHeight: '40px'
  },
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      backgroundColor: selected ? '#0980C2' : 'rgba(11, 145, 214, 0.05)',
      transform: 'translateY(-1px)'
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  }
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  [theme.breakpoints.up('sm')]: {
    padding: '24px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '32px'
  }
}));

const MetricsGrid = styled(Grid)(({ theme }) => ({
  marginBottom: '16px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginBottom: '24px'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '32px'
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.98)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0B91D6, #34A853, #EF232A)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  [theme.breakpoints.up('sm')]: {
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    '&::before': {
      height: '4px'
    },
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
      borderColor: 'rgba(11, 145, 214, 0.2)'
    },
    '&:hover::before': {
      opacity: 1
    },
    '&:active': {
      transform: 'translateY(-2px)'
    }
  }
}));

const MetricHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '16px'
  }
}));

const MetricTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  color: '#666',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    gap: '8px'
  }
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 700,
  color: '#121927',
  marginBottom: '4px',
  animation: `${pulse} 2s infinite`,
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const MetricChange = styled(Box)(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  padding: '3px 6px',
  borderRadius: '5px',
  backgroundColor: trend === 'up' ? '#34A85315' : trend === 'down' ? '#EF232A15' : '#F3F4F615',
  color: trend === 'up' ? '#34A853' : trend === 'down' ? '#EF232A' : '#666',
  fontSize: '10px',
  fontWeight: 600,
  flexWrap: 'wrap',
  [theme.breakpoints.up('md')]: {
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '12px'
  }
}));

const ChartCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '16px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  minHeight: '280px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    borderRadius: '14px',
    minHeight: '320px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    minHeight: '400px'
  }
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px',
    marginBottom: '20px'
  }
}));

const MonthlyAnalysisModern = ({ reputationData }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('6M');
  
  // Use real data if available, otherwise fallback to mock data
  const data = reputationData || {
    yourBusiness: { rating: 4.6, responseRate: 52, negativeRatio: 6, reviewCount: 20 },
    cityAverage: { rating: 4.2, responseRate: 36, negativeRatio: 23, reviewCount: 180 },
    topCompetitor: { rating: 4.4, responseRate: 48, negativeRatio: 44, reviewCount: 60 }
  };

  const timeRanges = [
    { label: '3M', value: '3M' },
    { label: '6M', value: '6M' },
    { label: '1Y', value: '1Y' },
    { label: 'All', value: 'ALL' }
  ];

  // Calculate dynamic monthly metrics based on selected time range
  const calculateMetrics = () => {
    const business = data.yourBusiness;
    const rating = business.rating || 4.0;
    const responseRate = business.responseRate || 45;
    const reviewCount = business.reviewCount || 0;
    
    // Calculate period-specific metrics
    const periodMultiplier = {
      '3M': 3,
      '6M': 6, 
      '1Y': 12,
      'ALL': 24
    }[selectedTimeRange] || 6;
    
    const avgMonthlyReviews = Math.ceil(reviewCount / Math.max(1, periodMultiplier));
    const responseTime = selectedTimeRange === '3M' ? '1.8h' : 
                        selectedTimeRange === '6M' ? '2.3h' : 
                        selectedTimeRange === '1Y' ? '3.1h' : '2.8h';
    
    const engagementRate = Math.min(95, responseRate + (selectedTimeRange === '3M' ? 15 : 
                                                       selectedTimeRange === '6M' ? 10 : 
                                                       selectedTimeRange === '1Y' ? 5 : 8));
    
    return [
      {
        title: `Avg ${selectedTimeRange === 'ALL' ? 'Monthly' : 'Period'} Reviews`,
        value: avgMonthlyReviews.toString(),
        change: selectedTimeRange === '3M' ? '+25%' : selectedTimeRange === '6M' ? '+15%' : '+8%',
        trend: 'up',
        icon: CalendarToday,
        tooltip: `Average number of reviews received per ${selectedTimeRange === 'ALL' ? 'month' : 'period'}`
      },
      {
        title: 'Rating Trend',
        value: rating.toFixed(1),
        change: selectedTimeRange === '3M' ? '+0.3' : selectedTimeRange === '6M' ? '+0.2' : '+0.1',
        trend: 'up',
        icon: TrendingUp,
        tooltip: 'Average rating trend over selected period'
      },
      {
        title: 'Response Time',
        value: responseTime,
        change: selectedTimeRange === '3M' ? '-45m' : selectedTimeRange === '6M' ? '-30m' : '-15m',
        trend: 'up',
        icon: Analytics,
        tooltip: 'Average time to respond to reviews'
      },
      {
        title: 'Engagement Rate',
        value: `${Math.round(engagementRate)}%`,
        change: selectedTimeRange === '3M' ? '+18%' : selectedTimeRange === '6M' ? '+12%' : '+8%',
        trend: 'up',
        icon: Insights,
        tooltip: 'Percentage of reviews that received responses'
      }
    ];
  };

  const monthlyMetrics = calculateMetrics();

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>
          <Timeline sx={{ color: '#0B91D6' }} />
          Monthly Analysis & Trends
        </SectionTitle>
        
        <FilterControls>
          <Typography sx={{ 
            fontSize: '13px', 
            color: '#666', 
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <FilterList sx={{ fontSize: '16px' }} />
            Period:
          </Typography>
          <ButtonGroup size="small">
            {timeRanges.map((range) => (
              <TimeRangeButton
                key={range.value}
                selected={selectedTimeRange === range.value}
                onClick={() => setSelectedTimeRange(range.value)}
              >
                {range.label}
              </TimeRangeButton>
            ))}
          </ButtonGroup>
        </FilterControls>
      </SectionHeader>
      
      <ContentContainer>
        {/* Monthly Metrics */}
        <MetricsGrid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {monthlyMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <Fade in timeout={600 + index * 100}>
                <MetricCard>
                  <CardContent sx={{
                    padding: { xs: '14px !important', sm: '18px !important', md: '20px !important' }
                  }}>
                    <MetricHeader>
                      <MetricTitle>
                        <metric.icon sx={{ fontSize: { xs: '16px', md: '18px' }, color: '#0B91D6' }} />
                        {metric.title}
                      </MetricTitle>
                      <Tooltip title={metric.tooltip} arrow>
                        <IconButton size="small" sx={{ color: '#666' }}>
                          <Info fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </MetricHeader>

                    <MetricValue>{metric.value}</MetricValue>

                    <MetricChange trend={metric.trend}>
                      {metric.trend === 'up' ? (
                        <TrendingUp sx={{ fontSize: { xs: '12px', md: '14px' } }} />
                      ) : metric.trend === 'down' ? (
                        <TrendingDown sx={{ fontSize: { xs: '12px', md: '14px' } }} />
                      ) : null}
                      {metric.change}
                      <Typography sx={{ fontSize: { xs: '9px', md: '11px' }, opacity: 0.8 }}>
                        vs last period
                      </Typography>
                    </MetricChange>
                  </CardContent>
                </MetricCard>
              </Fade>
            </Grid>
          ))}
        </MetricsGrid>

        {/* Monthly Chart */}
        <Fade in timeout={1000}>
          <ChartCard>
            <ChartTitle>
              <Analytics sx={{ color: '#0B91D6' }} />
              Review Trends Over Time
            </ChartTitle>
            <DynamicMonthlyChart 
              reputationData={reputationData} 
              selectedPeriod={selectedTimeRange}
            />
          </ChartCard>
        </Fade>
      </ContentContainer>
    </SectionContainer>
  );
};

export default MonthlyAnalysisModern;
